import * as React from 'react';

import {tag} from '@sail/icons/react/Icon';
import {Divider, Icon, tokens, view} from '@sail/ui';
import type {AgentCallResponse} from '@stripe-internal/conversations-client';
import {defineMessages, useIntl} from 'react-intl';

import {useWidget} from '../../../Widget/useWidget';

type CouponCardProps = {
  agentCallResponse: AgentCallResponse;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.coupon_card.title',
    description: 'Coupon Card title',
    defaultMessage: 'Coupon',
  },
  percentageBased: {
    id: 'conversations_ui.coupon_card.percentage_based',
    description: 'Coupon card percentage based header',
    defaultMessage: 'Percentage-based discount',
  },
  fixedAmount: {
    id: 'conversations_ui.coupon_card.fixed_amount',
    description: 'Coupon card fixed amount discount header',
    defaultMessage: 'Fixed amount discount',
  },
  off: {
    id: 'conversations_ui.coupon_card.off',
    description: 'Coupon card off label',
    defaultMessage: 'off',
  },
  exp: {
    id: 'conversations_ui.coupon_card.exp',
    description: 'Coupon card expiration prefix',
    defaultMessage: 'Exp',
  },
  copiedPromotionCode: {
    id: 'conversations_ui.coupon_card.copied_promotion_code',
    description: 'Toast message when promotion code is copied',
    defaultMessage: 'Copied promotion code',
  },
  upTo: {
    id: 'conversations_ui.coupon_card.up_to',
    description: 'Up to prefix for maximum discount amount',
    defaultMessage: 'up to',
  },
});

type CouponNameProps = {
  id: string;
  name: string | null | undefined;
};

const CouponName: React.FC<CouponNameProps> = ({id, name}) => {
  if (name) {
    return <>{name}</>;
  }
  return <>{id}</>;
};

type CouponDiscountProps = {
  amountOff: number | null | undefined;
  currency: string | null | undefined;
  percentOff: number | null | undefined;
};

const CouponDiscount: React.FC<CouponDiscountProps> = ({
  amountOff,
  currency,
  percentOff,
}) => {
  const intl = useIntl();

  // Percentage discount
  if (percentOff) {
    return (
      <>
        {percentOff}
        {'% '}
        {intl.formatMessage(messages.off)}
      </>
    );
  }

  // Fixed amount discount only
  if (amountOff && currency) {
    const amount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountOff / 100);
    return (
      <>
        {amount} {intl.formatMessage(messages.off)}
      </>
    );
  }

  return null;
};

type CouponCardData = any & {
  promotion_code: any | null | undefined;
};

const toCouponCardData = (
  response: AgentCallResponse,
): CouponCardData | null => {
  if (typeof response !== 'object' || !('object' in response)) {
    return null;
  }

  if (response.object === 'coupon') {
    const c = response as any & {
      promotion_code: any | null | undefined;
    };
    return c;
  }

  if (response.object === 'promotion_code') {
    const {coupon, ...p} = response as any & {coupon: any};

    return {...coupon, promotion_code: {...p, coupon}};
  }

  return null;
};

export const CouponCard = ({agentCallResponse}: CouponCardProps) => {
  const intl = useIntl();
  const widget = useWidget();

  const coupon = toCouponCardData(agentCallResponse);

  if (!coupon) {
    return null;
  }

  return (
    <view.div
      css={{
        backgroundColor: 'surface',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 1,
        borderStyle: 'solid',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      onClick={() => {
        if (!coupon.id) {
          return;
        }

        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/coupons/${coupon.id}`,
          },
        });
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'small',
          width: '100%',
          minWidth: 0,
        }}
      >
        <view.div
          css={{
            stack: 'x',
            gap: 'space.75',
            alignItems: 'center',
          }}
        >
          <view.div
            css={{
              stack: 'x',
              gap: 'xsmall',
              alignItems: 'center',
              width: 'max',
            }}
          >
            <Icon icon={tag} size="xsmall" css={{color: 'subdued'}} />
            <view.span
              css={{
                color: 'subdued',
                font: 'label.small.emphasized',
              }}
            >
              {intl.formatMessage(messages.cardTitle)}
            </view.span>
          </view.div>
          <Divider />
          <view.span
            css={{
              font: 'label.small',
              color: 'subdued',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <CouponName id={coupon.id} name={coupon.name} />
          </view.span>
        </view.div>
        <view.div css={{stack: 'x', gap: 'xsmall', alignItems: 'baseline'}}>
          <view.span
            css={{
              font: 'heading.large',
            }}
          >
            <CouponDiscount
              amountOff={coupon.amount_off}
              currency={coupon.currency}
              percentOff={coupon.percent_off}
            />
          </view.span>
        </view.div>
        <view.div
          css={{
            stack: 'x',
            gap: 'medium',
            alignItems: 'center',
            width: 'fill',
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          {coupon.promotion_code?.code ? (
            <view.div
              css={{
                minWidth: 'space.0',
                flex: '1 1 0',
              }}
            >
              <view.div
                css={{
                  stack: 'x',
                  gap: 'small',
                  justifyContent: 'space-between',
                  backgroundColor: 'offset',
                  alignItems: 'center',
                  paddingX: 'small',
                  paddingY: 'space.75',
                  color: 'text',
                  borderRadius: 'small',
                  overflow: 'hidden',
                  border: `1px solid ${tokens.color.neutral[50]}`,
                }}
              >
                <view.span
                  css={{
                    display: 'block',
                    font: 'label.small',
                    fontWeight: 'bold',
                    fontFamily: 'monospace',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {coupon.promotion_code?.code}
                </view.span>
              </view.div>
            </view.div>
          ) : null}
          {coupon.redeem_by ? (
            <view.div
              css={{
                stack: 'x',
                minWidth: 'space.0',
                maxWidth: 'max',
                flex: '2 1 0',
              }}
            >
              <view.span
                css={{
                  font: 'label.small',
                  color: 'subdued',
                  whiteSpace: 'nowrap',
                }}
              >
                {intl.formatMessage(messages.exp)}{' '}
                {intl.formatDate(new Date(coupon.redeem_by * 1000), {
                  year: 'numeric',
                  month: 'short',
                  day: '2-digit',
                })}
              </view.span>
            </view.div>
          ) : null}
        </view.div>
      </view.div>
    </view.div>
  );
};
